import * as React from 'react';

import { Button, ConsoleDialog } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { categoryCreateAtom } from '../../../_graphql/mutationCategoryCreate';
import {
  $canSubmitCategoryForm,
  $categoryForm,
  $resetCategoryForm,
} from '../../../_state/categoryForm.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { CategoryFormFields } from './CategoryFormFields';

interface CategoryCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CategoryCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: CategoryCreateDialogProps) => {
  const t = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useAtomValue($categoryForm);
  const canSubmit = useAtomValue($canSubmitCategoryForm);

  const resetForm = useSetAtom($resetCategoryForm);
  const createCategory = useSetAtom(categoryCreateAtom({}));

  const toast = useToast();

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    try {
      setIsSubmitting(true);
      await createCategory({
        name: form.name.trim(),
        description: form.description.trim(),
        attributes: form.attributes.map((attr) => ({
          ...attr,
          name: attr.name.trim(),
          description: attr.description?.trim() ?? '',
        })),
      });
      toast.success(t.inventoryView.categoryCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.inventoryView.categoryCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [createCategory, form, toast, onSuccess, handleClose, t]);

  return (
    <ConsoleDialog
      className="@CategoryCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.inventoryView.categoryCreateDialog.title}
      content={<CategoryFormFields disabled={isSubmitting} />}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.inventoryView.categoryCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.inventoryView.categoryCreateDialog.actions.creating
              : t.inventoryView.categoryCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
